import classNames from 'classnames';
import React, { useState } from 'react';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import NestableLink from '../utils/NestableLink';
import Button from './Button';
import OverflowMenu from './OverflowMenu';
import SVGIcon from './SVGIcon';
import * as styles from './SquarishCardBase.module.scss';

const MAX_DESCRIPTION_LINES = Number.parseInt(styles.maxDescriptionLines, 10);

interface SquarishCardBaseProps {
  icons?: React.ReactNode;
  avatars?: React.ReactNode;
  banner: React.ReactNode;
  overflowMenuItems?: React.ReactNode;
  title?: React.ReactNode;
  description: string;
  footer?: React.ReactNode;
  alwaysShowFooter?: boolean;
  linkTo?: string;
}

const SquarishCardBase = ({
  banner,
  avatars,
  icons,
  overflowMenuItems,
  title,
  description,
  footer,
  alwaysShowFooter,
  linkTo,
}: SquarishCardBaseProps) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  const content = (
    <div
      className={classNames(styles.container, styles.hoverContainer, {
        [styles.forceFooter]: alwaysShowFooter,
      })}
    >
      <div className={styles.header}>
        <div className={styles.banner}>
          {banner}
          {!!overflowMenuItems && (
            <div
              className={classNames(styles.overflowMenu, {
                [styles.isOpen]: isOverflowMenuOpen,
              })}
            >
              <OverflowMenu
                open={isOverflowMenuOpen}
                onOpenChange={setIsOverflowMenuOpen}
                placement="bottom-start"
                button={
                  <Button
                    className={styles.overflowMenuButton}
                    aria-label="More options"
                  >
                    <SVGIcon size={14}>{overflowMenuIcon}</SVGIcon>
                  </Button>
                }
              >
                {overflowMenuItems}
              </OverflowMenu>
            </div>
          )}
        </div>
        {!!(icons || avatars) && (
          <div className={styles.iconsBar}>
            {icons}
            <div className={styles.avatarContainer}>{avatars}</div>
          </div>
        )}
      </div>
      <div className={styles.content}>
        <div className={styles.title}>{title}</div>
        {/* Show as many description lines as can fit into the available space. Based on
            https://dev.to/maxart2501/css-tip-hide-content-in-short-containers-4obc */}
        {!!description &&
          Array.from({ length: MAX_DESCRIPTION_LINES }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className={styles.description}>
              <div
                className={styles.descriptionContent}
                data-max-lines={index + 1}
                aria-hidden
              >
                {description}
              </div>
            </div>
          ))}
      </div>
      <div className={styles.footer}>
        <div className={styles.footerContent}>{footer}</div>
      </div>
    </div>
  );

  if (!linkTo) {
    return content;
  }

  return (
    <NestableLink className={styles.hoverContainer} to={linkTo}>
      {content}
    </NestableLink>
  );
};

interface IconProps {
  icon: string;
}

/**
 * To set the icon background, use the `properties.$squarishCardIconBackground` css variable.
 */
const Icon = ({ icon }: IconProps) => (
  <SVGIcon className={styles.icon}>{icon}</SVGIcon>
);

SquarishCardBase.Icon = Icon;

export default SquarishCardBase;
